import { useRef, type CSSProperties, type ReactNode } from 'react';
import { useMeasureCssVariable } from '@src/components/layout/useMeasureCssVariable';
import { Button } from '@src/components/Button/Button';
import {
  Container,
  type ContainerGutter,
  type ContainerWidth,
} from '@src/components/Container/Container';
import { ChevronLeftIcon } from '@src/components/icons/ChevronLeftIcon';
import { classes } from '@src/components/layout/space';
import { Stack } from '@src/components/Stack/Stack';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './CreationHeader.css';

export interface CreationHeaderProps {
  /** The step the wizard is on — “Choose game name”, “Your picture”. */
  title: ReactNode;
  /** 1-based. Clamped to `steps`. */
  step: number;
  /** How many steps the wizard has. At least 1. */
  steps: number;
  /** Leave off to hide the chevron — a first screen with nowhere to go. */
  onBack?: () => void;
  backLabel?: string;
  /**
   * Right-hand slot. Same idea as AppHeader `tools`: the signed-in chip,
   * a sign-up button, or nothing.
   */
  account?: ReactNode;
  width?: ContainerWidth;
  gutter?: ContainerGutter;
  /** Stick to the viewport. Off for a nested specimen. */
  sticky?: boolean;
  /** Off for screens that are not stepped wizards — the quick-draw dashboard. */
  showProgress?: boolean;
  className?: string;
  testId?: string;
}

function clampStep(step: number, steps: number): { step: number; steps: number } {
  const total = Math.max(1, Math.floor(steps) || 1);
  const current = Math.min(total, Math.max(1, Math.floor(step) || 1));
  return { step: current, steps: total };
}

/**
 * Sticky chrome for a create flow — box, card, or quick draw. Back on the
 * left, the current step in the middle, the account on the right, and a
 * coral track for how far through the wizard we are.
 */
export function CreationHeader({
  title,
  step,
  steps,
  onBack,
  backLabel = 'Back',
  account,
  width = 'content',
  gutter = 'default',
  sticky = true,
  showProgress = true,
  className,
  testId,
}: CreationHeaderProps) {
  const progress = clampStep(step, steps);
  const headerRef = useRef<HTMLElement>(null);
  useMeasureCssVariable(headerRef, '--size-creation-header-measured', sticky);

  return (
    <header
      ref={headerRef}
      className={classes('creation-header', sticky && 'creation-header--sticky', className)}
      style={
        {
          '--creation-header-step': progress.step,
          '--creation-header-steps': progress.steps,
        } as CSSProperties
      }
      {...testIdAttr(testId)}
    >
      <Container width={width} gutter={gutter} pad="header">
        <div className="creation-header__bar">
          <div className="creation-header__side creation-header__side--start">
            {onBack ? (
              <Button
                variant="ghost"
                size="medium"
                icon={<ChevronLeftIcon />}
                aria-label={backLabel}
                onClick={onBack}
                testId={testIdFor(testId, 'back')}
              />
            ) : null}
          </div>
          <Stack gap={1} align="center" className="creation-header__center">
            <Typography as="p" role="title/sub" testId={testIdFor(testId, 'title')}>
              {title}
            </Typography>
          </Stack>
          <div className="creation-header__side creation-header__side--end">{account}</div>
        </div>
      </Container>
      {showProgress ? (
        <div
          className="creation-header__progress"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={progress.steps}
          aria-valuenow={progress.step}
          aria-valuetext={`${progress.step} / ${progress.steps}`}
          {...testIdAttr(testIdFor(testId, 'progress'))}
        >
          <span className="creation-header__progress-fill" />
        </div>
      ) : null}
    </header>
  );
}
